/**
 * Routes, and who may reach them.
 *
 * `RequireSession` answers "is anybody signed in"; `RequirePermission` answers
 * "may this person be here". Neither is security — the API enforces the same
 * rules on every call — they only keep someone off a screen that would fail to
 * load. URLs follow
 * `docs/ui-design.md` §4, with the
 * invented ones the live app grew kept alive as redirects.
 */

import { Suspense } from 'react';
import { BrowserRouter, Route, Routes } from 'react-router-dom';
import { AuthProvider } from './provider/auth';
import { PageTitleProvider } from './provider/pageTitle';
import { ToastProvider } from './provider/toast';
import { WorkspaceProvider } from './provider/workspace';
import Shell from './layouts/Shell';
import Auth from './pages/Auth';
import Clients, { AddClientPage } from './pages/Clients';
import ClientLibrary from './features/intake/ClientLibrary';
import ClientModulesPage from './features/modules/ClientModulesPage';
import ModuleHubPage from './features/modules/ModuleHubPage';
import FileDetail from './features/intake/FileDetail';
import UploadWizard from './features/intake/UploadWizard';
import ReviewRules from './features/intake/ReviewRules';
import FileProcessing from './features/intake/FileProcessing';
import MappingWorkspace from './features/mapping/MappingWorkspace';
import Administration from './features/admin/Administration';
import Dashboard from './pages/Dashboard';
import Approvals from './pages/Approvals';
import Activity from './pages/Activity';
import FirmConfiguration from './features/config/FirmConfiguration';
import Settings from './pages/Firm';
import AuditsList from './features/audits/AuditsList';
import CreateAudit from './features/audits/CreateAudit';
import AuditWorkspace from './features/audits/AuditWorkspace';
import AuditSettings from './features/audits/AuditSettings';
import FilesWorkspace from './features/files/FilesWorkspace';
import AuditFileDetail from './features/files/AuditFileDetail';
import AnalysesIndex from './features/analyses/AnalysesIndex';
import { AnalysisRoute } from './features/analyses/AnalysisPage';
import ExceptionsScreen from './features/exceptions/ExceptionsScreen';
import ReportWorkspace from './features/reports/ReportWorkspace';
import { LoadingState } from './features/shared/states';
import {
	AuditsForClient,
	Home,
	LegacyClientRedirect,
	NavigateKeepingQuery,
	LegacyFilesRedirect,
	LegacyAuditRedirect,
	LegacyHubRedirect,
	LegacyIntakeRedirect,
	LegacyModulePageRedirect,
	LegacyProcessingRedirect,
	LegacyReconciliationRedirect,
	RequireAudits,
	RequireIntake,
	RequireIntakeOrAudits,
	RequirePermission,
	RequireSession,
} from './routes/guards';

export default function App() {
	return (
		<BrowserRouter>
			<ToastProvider>
				<AuthProvider>
					<PageTitleProvider>
						<Suspense fallback={<LoadingState />}>
							<Routes>
								{/* `/sign-in` is the legacy URL the live app's guards still
                    redirect an unauthenticated visitor to (see
                    `RequireSession` and `Home` in `routes/guards.tsx`) — it is
                    not one of the five approved auth URLs below, so it stays,
                    now pointed at `Auth` since `SignIn` no longer exists.
                    `Auth` reads the path itself and renders its sign-in view
                    for anything that is not one of the other four. */}
								<Route path="/sign-in" element={<Auth />} />

								{/* The five approved auth URLs — spec §4's `pages/Auth.tsx`,
                    restored by task 12. One component, five routes: `Auth`
                    reads `useLocation()` and picks the view. */}
								<Route path="/login" element={<Auth />} />
								<Route path="/register" element={<Auth />} />
								<Route
									path="/accept-invite"
									element={<Auth />}
								/>
								<Route
									path="/forgot-password"
									element={<Auth />}
								/>
								<Route
									path="/reset-password"
									element={<Auth />}
								/>

								<Route
									element={
										<RequireSession>
											<WorkspaceProvider>
												<Shell />
											</WorkspaceProvider>
										</RequireSession>
									}
								>
									<Route path="/" element={<Home />} />

									<Route element={<RequireAudits />}>
										<Route
											path="/dashboard"
											element={<Dashboard />}
										/>
									</Route>

									{/* One Clients menu for every role: the list, a client's
                      module page and its hub (Documents / Audits) need intake
                      OR audits access; what lies under them is gated by side.
                      `/intake` and `/intake/clients/:id/modules` are old
                      front doors and redirect here. */}
									<Route element={<RequireIntakeOrAudits />}>
										<Route
											path="/clients"
											element={<Clients />}
										/>
										<Route
											path="/clients/:clientId/modules"
											element={<ClientModulesPage />}
										/>
									</Route>

									{/* Everything under one client in one module: its data
                      files (intake) and its audits, the module in the path. */}
									<Route path="/clients/:clientId/modules/:module">
										<Route
											element={<RequireIntakeOrAudits />}
										>
											<Route
												index
												element={<ModuleHubPage />}
											/>
										</Route>
										<Route
											path="data-files"
											element={<RequireIntake />}
										>
											<Route
												index
												element={<ClientLibrary />}
											/>
											<Route
												path="upload"
												element={<UploadWizard />}
											/>
											<Route
												path=":fileId"
												element={<FileDetail />}
											/>
										</Route>

										{/* Mapping is data-entry work, so it is gated on the
                        capability rather than on the place — an auditor
                        reaching this URL goes home. */}
										<Route
											path="data-files/:fileId/mapping"
											element={
												<RequirePermission need="mapFormats">
													<MappingWorkspace />
												</RequirePermission>
											}
										/>

										{/* The step between confirming a mapping and writing
                        345,000 rows: which rules will run, and what they make
                        of this file's own rows. Same gate as the mapping it
                        follows — and as the API it calls. */}
										<Route
											path="data-files/:fileId/review"
											element={
												<RequirePermission need="mapFormats">
													<ReviewRules />
												</RequirePermission>
											}
										/>

										{/* Watching a run is reading, not data entry — but the
                        only way to arrive here is to have just started one, so
                        it carries the same gate as the screen that starts it. */}
										<Route
											path="data-files/:fileId/processing"
											element={
												<RequirePermission need="mapFormats">
													<FileProcessing />
												</RequirePermission>
											}
										/>

										<Route
											path="audits"
											element={<RequireAudits />}
										>
											<Route
												index
												element={<AuditsList />}
											/>
											<Route
												path="new"
												element={<CreateAudit />}
											/>
											<Route
												path=":auditId"
												element={<AuditWorkspace />}
											>
												{/* The workspace opens on Analyses. The Data, Exceptions
                            and Report tabs are hidden; their routes stay
                            reachable by direct link. */}
												<Route
													index
													element={
														<NavigateKeepingQuery to="analyses" />
													}
												/>
												<Route
													path="data"
													element={<FilesWorkspace />}
												/>
												<Route
													path="data/:fileId"
													element={
														<AuditFileDetail />
													}
												/>
												<Route
													path="analyses"
													element={<AnalysesIndex />}
												/>
												{/* `/analyses/26` is an analysis, `/analyses/<slug>` one report. */}
												<Route
													path="analyses/:analysisSlug"
													element={<AnalysisRoute />}
												/>
												<Route
													path="exceptions"
													element={
														<ExceptionsScreen />
													}
												/>
												{/* Same screen, at the deep link: `ExceptionsScreen`
                            reads `useParams().exceptionId` itself and opens the
                            drawer straight to it — see that file's own header. */}
												<Route
													path="exceptions/:exceptionId"
													element={
														<ExceptionsScreen />
													}
												/>
												<Route
													path="report"
													element={
														<ReportWorkspace />
													}
												/>
												<Route
													path="details"
													element={<AuditSettings />}
												/>
												<Route
													path="settings"
													element={
														<NavigateKeepingQuery to="../details" />
													}
												/>
											</Route>
										</Route>
									</Route>
									<Route
										path="/clients/:clientId/home"
										element={<LegacyHubRedirect />}
									/>
									<Route
										path="/intake"
										element={
											<NavigateKeepingQuery to="/clients" />
										}
									/>
									<Route
										path="/intake/clients/:clientId/modules"
										element={<LegacyModulePageRedirect />}
									/>
									{/* `POST /clients` (`clientsController.create`) is
                      gated on `manageClients` — matched here so the
                      route guard agrees with what the API enforces. */}
									<Route
										path="/clients/new"
										element={
											<RequirePermission need="manageClients">
												<AddClientPage />
											</RequirePermission>
										}
									/>
									<Route
										path="/clients/:clientId"
										element={<LegacyClientRedirect />}
									/>

									{/* A client's data files moved under the client and module. */}
									<Route
										path="/intake/clients/:clientId/*"
										element={<LegacyIntakeRedirect />}
									/>

									{/* Audits live under their client and module; these are
                      the firm-wide pages and the old URLs, which redirect. */}
									<Route element={<RequireAudits />}>
										<Route
											path="/audits"
											element={
												<AuditsForClient>
													<AuditsList />
												</AuditsForClient>
											}
										/>
										<Route
											path="/audits/new"
											element={
												<AuditsForClient>
													<CreateAudit />
												</AuditsForClient>
											}
										/>
										<Route
											path="/audits/:auditId/*"
											element={<LegacyAuditRedirect />}
										/>
									</Route>

									{/* `GET /approvals` (`firmController.listApprovals`) is
                      itself gated on `generateReports` — not the broader
                      "any auditor may see it" split the approved screen's own
                      comment describes — so the route guard matches what the
                      API actually enforces rather than what would 403 on
                      arrival. */}
									<Route
										path="/approvals"
										element={
											<RequirePermission need="generateReports">
												<Approvals />
											</RequirePermission>
										}
									/>
									{/* `GET /activity` is open to anyone signed in (spec §4) —
                      no `RequirePermission` wrapper needed beyond the
                      `RequireSession` this whole subtree already sits inside. */}
									<Route
										path="/activity"
										element={<Activity />}
									/>
									{/* Firm configuration is for administrators only —
                      `manageUsers`, which only the Admin role has: the same
                      gate as People and as both rail entries in `Shell`. */}
									<Route
										path="/firm/configuration"
										element={
											<RequirePermission need="manageUsers">
												<FirmConfiguration />
											</RequirePermission>
										}
									/>
									<Route
										path="/firm/configuration/:tab"
										element={
											<RequirePermission need="manageUsers">
												<FirmConfiguration />
											</RequirePermission>
										}
									/>

									{/* Users and Maintenance were two rail entries acting on the
                      same records — a person's role in one, the deletion of
                      their account in the other. The approved `Administration`
                      screen covers the first; see that file's own header for
                      why the second (a hard delete of clients, engagements,
                      files and accounts) has no real endpoint here and is not
                      restored. */}
									<Route
										path="/administration"
										element={
											<RequirePermission need="manageUsers">
												<Administration />
											</RequirePermission>
										}
									/>
									<Route
										path="/administration/:tab"
										element={
											<RequirePermission need="manageUsers">
												<Administration />
											</RequirePermission>
										}
									/>

									<Route
										path="/account"
										element={<Settings />}
									/>
									<Route
										path="/settings"
										element={
											<NavigateKeepingQuery to="/account" />
										}
									/>

									{/* ── legacy redirects ─────────────────────────────────────
                      Kept outside every guard above: the visitor following one
                      of these old links may not hold the permission the NEW
                      url would require, and the point of a redirect is to land
                      them somewhere real regardless. */}
									<Route
										path="/people"
										element={
											<NavigateKeepingQuery to="/administration" />
										}
									/>
									{/* The lookup this would need (which client owns this file)
                      is not worth a fetch inside a redirect; intake is close
                      enough for a bookmark this old. */}
									<Route
										path="/files/:fileId/mapping"
										element={
											<NavigateKeepingQuery to="/clients" />
										}
									/>
									<Route
										path="/audits/:auditId/files"
										element={<LegacyFilesRedirect />}
									/>
									<Route
										path="/audits/:auditId/mapping/:fileId"
										element={
											<NavigateKeepingQuery to="/clients" />
										}
									/>
									<Route
										path="/audits/:auditId/reconciliation"
										element={
											<LegacyReconciliationRedirect />
										}
									/>
									{/* Processing was a tab of its own until Task T5 merged it
                      into Analyses — see that task's own note on
                      `AuditWorkspace`'s tab strip. */}
									<Route
										path="/audits/:auditId/processing"
										element={<LegacyProcessingRedirect />}
									/>
									<Route
										path="/team"
										element={
											<NavigateKeepingQuery to="/administration" />
										}
									/>
									<Route
										path="/maintenance"
										element={
											<NavigateKeepingQuery to="/administration" />
										}
									/>
								</Route>

								<Route
									path="*"
									element={
										<NavigateKeepingQuery
											to="/"
											moduleOnly
										/>
									}
								/>
							</Routes>
						</Suspense>
					</PageTitleProvider>
				</AuthProvider>
			</ToastProvider>
		</BrowserRouter>
	);
}
